import type AuthProvider from '$lib/components/auth/store.svelte';
import type { ICartItem } from '$lib/components/cart/store.svelte';

export class TikTokAdsProvider {
	protected storeId: number;
	protected storeName: string;
	protected storeCurrency: string;
	protected clientIp: string;
	protected clientUserAgent: string | null;
	protected auth: AuthProvider;

	constructor(store: Config, clientIp: string, clientUserAgent: string | null, auth: AuthProvider) {
		this.storeId = store.store_id;
		this.storeName = store.store_name;
		this.storeCurrency = store.currency;
		this.clientIp = clientIp;
		this.clientUserAgent = clientUserAgent;
		this.auth = auth;
	}

	async #trackEvent(
		eventName: string,
		eventId: string,
		eventParams: Record<string, unknown>,
		value: number,
	) {
		if (typeof ttq !== 'undefined') {
			const userData = this.auth.userData;
			ttq.identify({
				email: userData?.hashedEmail,
				ip: this.clientIp,
				user_agent: this.clientUserAgent ?? undefined,
			});

			ttq.track(
				eventName,
				{
					contents: [eventParams],
					value: value,
					currency: this.storeCurrency,
				},
				{
					event_id: (await this.#hash(eventId)) + eventName,
				},
			);
		}
	}

	async trackInitiateCheckout(
		cartItems: ICartItem[],
		totalQuantity: number,
		totalPrice: number,
		creationDate: Date,
	) {
		if (cartItems.length == 0) {
			return;
		}

		const contentId = this.storeId + '-' + creationDate;
		const eventId = Date.now() + '-' + contentId;

		await this.#trackEvent(
			'InitiateCheckout',
			eventId,
			{
				content_id: contentId,
				content_ids: cartItems.map((item) => item.sku),
				content_type: 'product',
				contents: cartItems.map((item) => ({
					content_id: item.sku,
					content_name: item.name,
					price: item.unit_price,
					brand: item.brand,
				})),
				price: totalPrice,
				currency: this.storeCurrency,
				num_items: totalQuantity,
				description: 'Initiate Checkout',
			},
			totalPrice,
		);
	}

	async trackAddToCart(variant: Variant, productName: string, totalQuantity: number) {
		const price = variant.special_price ?? variant.price;
		const eventId = Date.now() + '-' + this.storeId + '-' + variant.data.sku;
		const data = {
			content_type: 'product',
			num_items: totalQuantity,
			description: productName,
			content_ids: [variant.data.sku],
			content_id: variant.data.sku,
			currency: this.storeCurrency,
			price: price,
		};

		await this.#trackEvent('AddToCart', eventId, data, price!);
	}

	async trackViewContent(product: Product, price: number) {
		const eventId = Date.now() + '-' + this.storeId + '-' + product.data.sku;
		const data = {
			content_type: 'product',
			content_ids: [product.data.sku],
			content_id: product.data.sku,
			content_name: product.data.name,
			price: price,
			currency: this.storeCurrency,
		};

		await this.#trackEvent('ViewContent', eventId, data, price);
	}

	async trackPurchase(order: SalesOrder) {
		const eventId = Date.now() + '-' + this.storeId + '-' + order.ref;
		const data = {
			content_id: order.ref,
			content_ids: order.items.map((item) => item.sku),
			content_type: 'product',
			contents: order.items.map((item) => ({
				content_id: item.sku,
				content_name: item.name,
				price: item.unit_price,
				brand: item.brand,
			})),
			currency: order.currency,
			price: order.grand_total,
			num_items: order.items.reduce((acc, item) => acc + item.quantity, 0),
			order_id: order.ref,
			description: 'Order success page',
			shop_id: this.storeName,
		};

		await this.#trackEvent('Purchase', eventId, data, order.grand_total);
	}

	async #hash(value: string): Promise<string> {
		const encoder = new TextEncoder();
		const data = encoder.encode(value);
		return crypto.subtle.digest('SHA-256', data).then((hash) => {
			return Array.from(new Uint8Array(hash))
				.map((b) => b.toString(16).padStart(2, '0'))
				.join('');
		});
	}
}

export default TikTokAdsProvider;
